import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, IconButton, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import { QRCodeCanvas } from 'qrcode.react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { QrSettings } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

const DEFAULTS: Required<QrSettings> = {
  company_name: '',
  tagline: '',
  footer_text: 'Scan to view in the mobile app',
  logo_url: null,
  fg_color: '#111827',
  bg_color: '#FFFFFF',
  error_correction: 'M',
}

export function QrTab() {
  const qc = useQueryClient()
  const { org, prefs } = useOrgSettings()
  const initial = { ...DEFAULTS, ...(prefs.qr ?? {}) }
  const [draft, setDraft] = useState<Required<QrSettings>>(initial)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoBroken, setLogoBroken] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Re-seed local state if the org reloads after a save (or another tab
  // mutates settings). useOrgSettings memoizes prefs on identity, so this
  // only fires when the server-side qr block actually changes.
  useEffect(() => {
    setDraft({ ...DEFAULTS, ...(prefs.qr ?? {}) })
    setLogoBroken(false)
  }, [prefs.qr])

  const save = useMutation({
    mutationFn: () => {
      // Org preferences are stored as one JSONB blob — merge the qr
      // subkey into the rest before sending so we don't wipe unrelated
      // settings (currency, timezone, prefixes, etc.).
      const next = { ...(org?.preferences ?? {}), qr: draft }
      return api.orgs.update({ preferences: next })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] })
      toast.success('QR branding saved')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  async function handleLogo(file: File) {
    setLogoUploading(true)
    setLogoBroken(false)
    try {
      const { url } = await api.orgs.uploadQrLogo(file)
      setDraft((d) => ({ ...d, logo_url: url }))
      toast.success('Logo uploaded — remember to Save')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setLogoUploading(false)
    }
  }

  return (
    <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 4 }}>
      {/* Form column */}
      <Stack spacing={3} sx={{ maxWidth: 560 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>QR branding</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Applied to every printable QR card — assets, work orders, and locations.
          </Typography>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Company name"
            value={draft.company_name}
            onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
            size="small" fullWidth
            placeholder="Shown above the QR"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Tagline (optional)"
            value={draft.tagline}
            onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
            size="small" fullWidth
            placeholder="One short line under the company name"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Footer text"
            value={draft.footer_text}
            onChange={(e) => setDraft({ ...draft, footer_text: e.target.value })}
            size="small" fullWidth
            placeholder="e.g. 'Scan to view in the mobile app'"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        {/* Logo */}
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Logo</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 72, height: 72, borderRadius: 1.5, border: '1px solid', borderColor: logoBroken ? 'error.main' : 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff',
              overflow: 'hidden',
            }}>
              {draft.logo_url ? (
                logoBroken ? (
                  <BrokenImageOutlinedIcon sx={{ fontSize: 28, color: 'error.main' }} />
                ) : (
                  <img src={draft.logo_url} alt="Logo"
                    onLoad={() => setLogoBroken(false)}
                    onError={() => setLogoBroken(true)}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                )
              ) : (
                <QrCode2OutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
              )}
            </Box>
            <Stack spacing={0.75}>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleLogo(f)
                  e.target.value = ''
                }}
              />
              <Button
                variant="outlined" size="small"
                disabled={logoUploading}
                startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => fileRef.current?.click()}>
                {logoUploading ? 'Uploading…' : draft.logo_url ? 'Replace logo' : 'Upload logo'}
              </Button>
              {draft.logo_url && (
                <Button
                  size="small" color="error"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  onClick={() => { setDraft({ ...draft, logo_url: null }); setLogoBroken(false) }}>
                  Remove
                </Button>
              )}
            </Stack>
          </Stack>

          {/* URL surface — clickable so the user can verify the bucket
              is actually reachable from their browser. If the on-screen
              <img> doesn't load (mixed content, VPN-only host, CORS,
              broken nginx proxy), this gives them a one-click way to
              see exactly what's failing. */}
          {draft.logo_url && (
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ fontSize: 11, color: logoBroken ? 'error.main' : 'text.disabled' }}>
              {logoBroken && <span><strong>Image failed to load.</strong>&nbsp;</span>}
              <Box component="a"
                href={draft.logo_url} target="_blank" rel="noopener noreferrer"
                sx={{
                  color: 'inherit', textDecoration: 'underline',
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  fontFamily: 'ui-monospace, monospace',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360,
                }}>
                {draft.logo_url}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Box>
            </Stack>
          )}
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
            PNG, JPG, or WebP. Resized to ≤1600px on upload.
          </Typography>
        </Stack>

        {/* Colors */}
        <Stack direction="row" spacing={2}>
          <ColorField
            label="Foreground"
            value={draft.fg_color}
            onChange={(v) => setDraft({ ...draft, fg_color: v })}
          />
          <ColorField
            label="Background"
            value={draft.bg_color}
            onChange={(v) => setDraft({ ...draft, bg_color: v })}
          />
        </Stack>

        {/* Error correction */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            Error correction
          </Typography>
          <Select
            value={draft.error_correction}
            onChange={(e) => setDraft({ ...draft, error_correction: e.target.value as Required<QrSettings>['error_correction'] })}
            size="small" fullWidth>
            <MenuItem value="L">Low (~7% — smallest dot density)</MenuItem>
            <MenuItem value="M">Medium (~15%) — recommended</MenuItem>
            <MenuItem value="Q">Quartile (~25%) — for printed asset tags</MenuItem>
            <MenuItem value="H">High (~30%) — survives scratches & dirt</MenuItem>
          </Select>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
            Higher correction = more redundant data, still scans if the print is damaged.
          </Typography>
        </Box>

        <Box>
          <Button
            variant="contained"
            disabled={save.isPending}
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Stack>

      {/* Live preview */}
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
          Preview
        </Typography>
        <PreviewCard draft={draft} />
      </Box>
    </Box>
  )
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 1 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1, border: '1px solid', borderColor: 'divider',
          bgcolor: value, flexShrink: 0,
          position: 'relative', cursor: 'pointer',
        }}>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
        </Box>
        <TextField size="small" value={value} onChange={(e) => onChange(e.target.value)}
          sx={{ flex: 1 }} inputProps={{ style: { fontFamily: 'ui-monospace, monospace', fontSize: 13 } }} />
      </Stack>
    </Box>
  )
}

function PreviewCard({ draft }: { draft: Required<QrSettings> }) {
  return (
    <Box sx={{
      width: 280, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
      bgcolor: draft.bg_color, mx: 'auto', textAlign: 'center',
    }}>
      {draft.company_name && (
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: draft.fg_color, mb: 0.25 }}>
          {draft.company_name}
        </Typography>
      )}
      {draft.tagline && (
        <Typography sx={{ fontSize: 11, color: draft.fg_color, opacity: 0.7, mb: 1.5 }}>
          {draft.tagline}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
        <QRCodeCanvas
          value="https://cmms.example/preview"
          size={180}
          level={draft.error_correction}
          fgColor={draft.fg_color}
          bgColor={draft.bg_color}
        />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: draft.fg_color }}>
        Sample asset name
      </Typography>
      <Typography sx={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: draft.fg_color, opacity: 0.6 }}>
        AST-12345
      </Typography>
      {draft.footer_text && (
        <Typography sx={{ fontSize: 10.5, color: draft.fg_color, opacity: 0.6, mt: 1.25 }}>
          {draft.footer_text}
        </Typography>
      )}
      {draft.logo_url && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
          <img src={draft.logo_url} alt="Logo"
            style={{ maxHeight: 36, maxWidth: 180, objectFit: 'contain' }} />
        </Box>
      )}
    </Box>
  )
}
